import { useMemo } from "react"
import { Bar, Line, Pie, Chart as ChartByType } from "react-chartjs-2"
import type { ChartData, ChartOptions } from "chart.js"

export const CHART_TYPES = ["bar", "line", "pie", "candlestick"] as const
export type ChartType = (typeof CHART_TYPES)[number]

export interface ChartSpec {
  type: ChartType
  title: string
  // Pie has no axes, so these are optional — validator enforces the per-type rule.
  xLabel?: string
  yLabel?: string
  data: Array<Record<string, unknown>>
}

interface ChartRendererProps {
  spec: ChartSpec
}

// Read CSS brand tokens synchronously at render — this app is pure CSR (Vite),
// no SSR, so getComputedStyle is always defined and avoids a useEffect hop.
function readBrandTokens() {
  const style = getComputedStyle(document.documentElement)
  return {
    coral: style.getPropertyValue("--color-brand-coral").trim(),
    ink: style.getPropertyValue("--color-brand-ink").trim(),
    inkSoft: style.getPropertyValue("--color-brand-ink-soft").trim(),
    muted: style.getPropertyValue("--muted-foreground").trim(),
    border: style.getPropertyValue("--border").trim(),
  }
}

// Accept common column-name aliases so charts render whether the agent used
// the contract's generic keys or more domain-meaningful ones.
const LABEL_KEYS = ["label", "name", "category", "x"] as const
const VALUE_KEYS = ["value", "y", "amount", "count", "total", "sales", "revenue", "size"] as const

function pickLabel(row: Record<string, unknown>): string {
  for (const k of LABEL_KEYS) {
    if (k in row && row[k] != null) return String(row[k])
  }
  return ""
}

function pickValue(row: Record<string, unknown>): number {
  for (const k of VALUE_KEYS) {
    const v = row[k]
    if (v != null) {
      const n = Number(v)
      if (Number.isFinite(n)) return n
    }
  }
  return 0
}

// Accept either the contract's single-letter OHLC keys or the long form.
const OHLC_KEYS = {
  o: ["o", "open"],
  h: ["h", "high"],
  l: ["l", "low"],
  c: ["c", "close"],
} as const

function pickOhlc(row: Record<string, unknown>, axis: keyof typeof OHLC_KEYS): number {
  for (const k of OHLC_KEYS[axis]) {
    const v = row[k]
    if (v != null) {
      const n = Number(v)
      if (Number.isFinite(n)) return n
    }
  }
  return 0
}

// Accept epoch ms (number or numeric string) or an ISO date string; return NaN
// on anything else so callers can branch on Number.isFinite.
function coerceEpochMs(raw: unknown): number {
  if (typeof raw === "number") return raw
  if (typeof raw === "string") {
    const asNum = Number(raw)
    if (Number.isFinite(asNum)) return asNum
    const parsed = Date.parse(raw)
    return Number.isFinite(parsed) ? parsed : NaN
  }
  return NaN
}

function buildCommonOptions(
  spec: ChartSpec,
  tokens: ReturnType<typeof readBrandTokens>
): ChartOptions {
  return {
    responsive: true,
    maintainAspectRatio: false,
    // Pin dpr to 1 and debounce resize callbacks — avoids a Chart.js feedback
    // loop where backing-store rounding nudges the parent wider every redraw.
    devicePixelRatio: 1,
    resizeDelay: 200,
    plugins: {
      legend: { labels: { color: tokens.inkSoft } },
      title: { display: true, text: spec.title, color: tokens.ink },
      tooltip: {},
    },
    scales: {
      x: {
        title: { display: true, text: spec.xLabel, color: tokens.ink },
        ticks: { color: tokens.inkSoft },
        grid: { color: tokens.muted },
        border: { color: tokens.border },
      },
      y: {
        title: { display: true, text: spec.yLabel, color: tokens.ink },
        ticks: { color: tokens.inkSoft },
        grid: { color: tokens.muted },
        border: { color: tokens.border },
      },
    },
  }
}

export function ChartRenderer({ spec }: ChartRendererProps) {
  const tokens = useMemo(readBrandTokens, [])
  // Key on a content hash so chart.update() only fires when data actually
  // changes, not on every parent re-render.
  const sig = JSON.stringify(spec)
  const chartContent = useMemo(() => {
    if (spec.type === "bar") {
      const data: ChartData<"bar"> = {
        labels: spec.data.map(pickLabel),
        datasets: [
          {
            label: spec.yLabel,
            data: spec.data.map(pickValue),
            backgroundColor: tokens.coral,
          },
        ],
      }
      return <Bar data={data} options={buildCommonOptions(spec, tokens) as ChartOptions<"bar">} />
    }
    if (spec.type === "line") {
      const data: ChartData<"line"> = {
        labels: spec.data.map(pickLabel),
        datasets: [
          {
            label: spec.yLabel,
            data: spec.data.map(pickValue),
            borderColor: tokens.coral,
            backgroundColor: tokens.coral,
          },
        ],
      }
      return <Line data={data} options={buildCommonOptions(spec, tokens) as ChartOptions<"line">} />
    }
    if (spec.type === "pie") {
      // Cycle the 5-colour brand palette so pies with >5 slices still shade
      // distinctly rather than all-falling-back to the last colour.
      const palette = [tokens.coral, tokens.inkSoft, tokens.ink, tokens.muted, tokens.border]
      const data: ChartData<"pie"> = {
        labels: spec.data.map(pickLabel),
        datasets: [
          {
            label: spec.yLabel,
            data: spec.data.map(pickValue),
            backgroundColor: spec.data.map((_, i) => palette[i % palette.length]),
          },
        ],
      }
      // Pie charts don't use cartesian scales — strip them from options.
      const opts = buildCommonOptions(spec, tokens)
      return <Pie data={data} options={{ ...opts, scales: undefined } as ChartOptions<"pie">} />
    }
    // candlestick — use the typed-chart form because react-chartjs-2 has no <Candlestick> shortcut.
    const data = {
      datasets: [
        {
          label: spec.title,
          data: spec.data.map(r => ({
            x: coerceEpochMs(r.x),
            o: pickOhlc(r, "o"),
            h: pickOhlc(r, "h"),
            l: pickOhlc(r, "l"),
            c: pickOhlc(r, "c"),
          })),
        },
      ],
    } as unknown as ChartData<"candlestick">
    const opts = {
      ...buildCommonOptions(spec, tokens),
      scales: {
        x: {
          type: "timeseries" as const,
          title: { display: true, text: spec.xLabel, color: tokens.ink },
        },
        y: { title: { display: true, text: spec.yLabel, color: tokens.ink } },
      },
    } as unknown as ChartOptions<"candlestick">
    return <ChartByType type="candlestick" data={data} options={opts} />
  }, [sig])

  return (
    // min-w-0 lets the flex bubble parent clamp our width; overflow-hidden
    // is a safety net if the canvas briefly mis-measures on first paint.
    <div className="my-2 w-full min-w-0 overflow-hidden rounded-md border border-border bg-white p-2">
      {/* contain: size layout isolates canvas-size writes from the outer tree. */}
      <div
        style={{
          position: "relative",
          width: "100%",
          height: "280px",
          contain: "size layout",
          overflow: "hidden",
        }}
      >
        {chartContent}
      </div>
      <ChartA11yTable spec={spec} />
    </div>
  )
}

function ChartA11yTable({ spec }: { spec: ChartSpec }) {
  if (spec.type === "candlestick") {
    return (
      <table className="sr-only !w-px !min-w-0">
        <caption>{spec.title}</caption>
        <thead>
          <tr>
            <th scope="col">Date</th>
            <th scope="col">Open</th>
            <th scope="col">High</th>
            <th scope="col">Low</th>
            <th scope="col">Close</th>
          </tr>
        </thead>
        <tbody>
          {spec.data.map((row, i) => {
            const epoch = coerceEpochMs(row.x)
            const dateCell = Number.isFinite(epoch)
              ? new Date(epoch).toISOString()
              : String(row.x ?? "")
            return (
              <tr key={i}>
                <td>{dateCell}</td>
                <td>{String(pickOhlc(row, "o"))}</td>
                <td>{String(pickOhlc(row, "h"))}</td>
                <td>{String(pickOhlc(row, "l"))}</td>
                <td>{String(pickOhlc(row, "c"))}</td>
              </tr>
            )
          })}
        </tbody>
      </table>
    )
  }
  return (
    <table className="sr-only !w-px !min-w-0">
      <caption>{spec.title}</caption>
      <thead>
        <tr>
          <th scope="col">{spec.xLabel}</th>
          <th scope="col">{spec.yLabel}</th>
        </tr>
      </thead>
      <tbody>
        {spec.data.map((row, i) => (
          <tr key={i}>
            <td>{pickLabel(row)}</td>
            <td>{String(pickValue(row))}</td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}
